import type { ScopeData } from './types'
import type { HourSettings } from '../settings/types'

export interface GeneratedHourEntry {
  id: string
  section: string
  feature: string
  hours: number
  source: 'auto' | 'manual'
}

export function calculateAutoHours(scope: ScopeData, hourSettings: HourSettings): GeneratedHourEntry[] {
  const entries: GeneratedHourEntry[] = []
  let idCounter = 1

  const addEntry = (section: string, feature: string, hours: number) => {
    if (hours > 0) {
      entries.push({
        id: `auto-${idCounter++}`,
        section,
        feature,
        hours,
        source: 'auto',
      })
    }
  }

  // === General Setup ===
  addEntry('General', 'Project Setup', hourSettings.general.projectSetup)

  if (
    scope.generalInfo.parentChild !== 'unknown' &&
    scope.generalInfo.parentChild !== 'standalone'
  ) {
    addEntry('General', 'Parent/Child Configuration', hourSettings.general.parentChildConfig)
  }

  // === KYC Configuration ===
  const kyc = scope.kycConfig
  const normalizeTri = (value: unknown) => {
    if (value === 'yes' || value === 'no' || value === 'unknown') return value
    if (value === 'on_behalf') return 'yes'
    if (value === true) return 'yes'
    if (value === false) return 'no'
    return 'unknown'
  }
  const isYes = (value: unknown) => normalizeTri(value) === 'yes'
  addEntry('KYC', 'Base Setup', hourSettings.kyc.baseSetup)

  // AU Document Types
  if (kyc.documentTypesAu.includes('passport')) {
    addEntry('KYC', 'AU - Passport', hourSettings.kyc.auDocumentPassport)
  }
  if (kyc.documentTypesAu.includes('drivers_license')) {
    addEntry('KYC', 'AU - Drivers License', hourSettings.kyc.auDocumentDriversLicense)
  }
  if (kyc.documentTypesAu.includes('medicare')) {
    addEntry('KYC', 'AU - Medicare Card', hourSettings.kyc.auDocumentMedicare)
  }
  if (kyc.documentTypesAu.includes('foreign_passport')) {
    addEntry('KYC', 'AU - Foreign Passport', hourSettings.kyc.auDocumentForeignPassport)
  }
  if (kyc.documentTypesAu.includes('birth_certificate')) {
    addEntry('KYC', 'AU - Birth Certificate', hourSettings.kyc.auDocumentBirthCertificate)
  }

  // NZ Document Types
  if (isYes(kyc.acceptNzCustomers)) {
    if (kyc.documentTypesNz.includes('passport')) {
      addEntry('KYC', 'NZ - Passport', hourSettings.kyc.nzDocumentPassport)
    }
    if (kyc.documentTypesNz.includes('drivers_license')) {
      addEntry('KYC', 'NZ - Drivers License', hourSettings.kyc.nzDocumentDriversLicense)
    }
    if (kyc.documentTypesNz.includes('birth_certificate')) {
      addEntry('KYC', 'NZ - Birth Certificate', hourSettings.kyc.nzDocumentBirthCertificate)
    }
  }

  // KYC Additional Features
  if (isYes(kyc.enableConsentSources)) {
    addEntry('KYC', 'Consent Sources', hourSettings.kyc.consentSources)
  }
  if (kyc.dvsForeignPassport === 'dvs') {
    addEntry('KYC', 'DVS Setup', hourSettings.kyc.dvsSetup)
  }
  if (kyc.dvsForeignPassport === 'vevo') {
    addEntry('KYC', 'VEVO Integration', hourSettings.kyc.vevoIntegration)
  }
  const intlCountries = kyc.internationalCountries.filter((c) => c !== 'None' && c !== "Don't know")
  if (intlCountries.length > 0) {
    addEntry(
      'KYC',
      `International KYC (${intlCountries.length} countries)`,
      hourSettings.kyc.internationalKycPerCountry * intlCountries.length
    )
  }
  if (kyc.ruleset === 'custom') {
    addEntry('KYC', 'Custom Ruleset', hourSettings.kyc.customRuleset)
  }
  if (kyc.vendor && kyc.vendor !== 'frankieone') {
    addEntry('KYC', `Vendor Integration (${kyc.vendor})`, hourSettings.kyc.vendorIntegration)
  }

  // KYC — Additional Rulesets (beyond the first one)
  if (Array.isArray(kyc.rulesets) && kyc.rulesets.length > 1) {
    addEntry('KYC', `Additional Rulesets (${kyc.rulesets.length - 1})`,
      hourSettings.kyc.perAdditionalRuleset * (kyc.rulesets.length - 1))
  }

  // KYC — Manual Review Triggers
  if (kyc.manualReviewTriggers) {
    const activeReviewTriggers = Object.values(kyc.manualReviewTriggers).filter((v) => v === 'yes').length
    if (activeReviewTriggers > 0) {
      addEntry('KYC', `Manual Review Triggers (${activeReviewTriggers})`, hourSettings.kyc.manualReviewSetup)
    }
  }

  // KYC — Cascade Configuration
  if (kyc.enableCascade === 'yes') {
    addEntry('KYC', 'Cascade Configuration', hourSettings.kyc.cascadeSetup)
  }

  // KYC — Data Sources
  if (Array.isArray(kyc.selectedDataSources) && kyc.selectedDataSources.length > 0) {
    addEntry('KYC', `Data Sources (${kyc.selectedDataSources.length})`,
      hourSettings.kyc.perDataSource * kyc.selectedDataSources.length)
  }

  // KYC — Aging Configuration
  if (kyc.agingEnabled) {
    addEntry('KYC', 'Aging Configuration', hourSettings.kyc.agingSetup)
  }

  // === AML Configuration ===
  const aml = scope.amlConfig
  if (aml.level && aml.level !== 'na') {
    addEntry('AML', 'Base Setup', hourSettings.aml.baseSetup)

    if (aml.level === 'standard') {
      addEntry('AML', 'Standard Level', hourSettings.aml.standardLevel)
    }
    if (aml.level === 'enhanced') {
      addEntry('AML', 'Enhanced Level', hourSettings.aml.enhancedLevel)
    }

    // PEP Levels
    if (aml.pepLevels.includes('1')) {
      addEntry('AML', 'PEP Level 1', hourSettings.aml.pepLevel1)
    }
    if (aml.pepLevels.includes('2')) {
      addEntry('AML', 'PEP Level 2', hourSettings.aml.pepLevel2)
    }
    if (aml.pepLevels.includes('3')) {
      addEntry('AML', 'PEP Level 3', hourSettings.aml.pepLevel3)
    }
    if (aml.pepLevels.includes('4')) {
      addEntry('AML', 'PEP Level 4', hourSettings.aml.pepLevel4)
    }

    if (aml.monitoringType === 'ongoing') {
      addEntry('AML', 'Ongoing Monitoring', hourSettings.aml.ongoingMonitoring)
    }
    if (aml.customBlockList) {
      addEntry('AML', 'Custom Block List', hourSettings.aml.customSanctions)
    }
    if (aml.vendor && aml.vendor !== 'frankieone') {
      addEntry('AML', `Vendor Integration (${aml.vendor})`, hourSettings.aml.vendorIntegration)
    }

    // AML — Sanctions Screening
    if (isYes(aml.sanctionsScreeningConfig?.enabled)) {
      addEntry('AML', 'Sanctions Screening', hourSettings.aml.sanctionsScreening)
    }

    // AML — Adverse Media Screening
    if (isYes(aml.adverseMediaScreeningConfig?.enabled)) {
      addEntry('AML', 'Adverse Media Screening', hourSettings.aml.adverseMediaScreening)
    }

    // AML — Additional Watchlists
    if (isYes(aml.additionalWatchlistsConfig?.enabled)) {
      addEntry('AML', 'Additional Watchlists', hourSettings.aml.additionalWatchlists)
    }

    // AML — Batch Processing
    if (aml.batchProcessing?.required === 'required_regularly' || aml.batchProcessing?.required === 'required_once') {
      addEntry('AML', 'Batch Processing', hourSettings.aml.batchProcessing)
    }

    // AML — Vendor-Specific Features
    if (aml.vendorSpecificFeatures) {
      const hasVendorFeatures =
        Object.values(aml.vendorSpecificFeatures.complyAdvantage || {}).some(Boolean) ||
        Object.values(aml.vendorSpecificFeatures.refinitiv || {}).some(Boolean) ||
        Object.values(aml.vendorSpecificFeatures.dowJones || {}).some(Boolean) ||
        Object.values(aml.vendorSpecificFeatures.acuris || {}).some(Boolean)
      if (hasVendorFeatures) {
        addEntry('AML', 'Vendor-Specific Features', hourSettings.aml.vendorSpecificSetup)
      }
    }

    // AML — Aging Configuration
    if (aml.agingEnabled) {
      addEntry('AML', 'Aging Configuration', hourSettings.aml.agingSetup)
    }
  }

  // === Biometrics/IDV Configuration ===
  const bio = scope.biometricsConfig
  if (bio.enabled === 'yes') {
    addEntry('Biometrics', 'Base Setup', hourSettings.biometrics.baseSetup)

    // Service Level (prefer flows; fallback to legacy serviceLevel)
    if (Array.isArray(bio.flows) && bio.flows.length > 0) {
      const flowHours = bio.flows.reduce((total, f) => {
        if (f.idVerificationEnabled !== 'yes') return total
        return total + (f.biometricsEnabled === 'yes' ? hourSettings.biometrics.documentBiometricsReview : hourSettings.biometrics.documentReview)
      }, 0)
      addEntry('Biometrics', `Flows (${bio.flows.length})`, flowHours)
    } else {
      if (bio.serviceLevel === 'document_review') {
        addEntry('Biometrics', 'Document + Review', hourSettings.biometrics.documentReview)
      }
      if (bio.serviceLevel === 'document_biometrics_review') {
        addEntry('Biometrics', 'Document + Biometrics + Review', hourSettings.biometrics.documentBiometricsReview)
      }
    }

    // Document Types
    if (bio.documentTypes.length > 0) {
      addEntry('Biometrics', `Document Types (${bio.documentTypes.length})`,
        hourSettings.biometrics.perDocumentType * bio.documentTypes.length)
    }

    // Frontend
    if (bio.frontend === 'hosted_onesdk') {
      addEntry('Biometrics', 'Hosted OneSDK', hourSettings.biometrics.hostedOneSdk)
    }
    if (bio.frontend === 'embedded_onesdk') {
      addEntry('Biometrics', 'Embedded OneSDK', hourSettings.biometrics.embeddedOneSdk)
    }
    if (bio.frontend === 'other') {
      addEntry('Biometrics', 'Other Frontend', hourSettings.biometrics.otherFrontend)
    }

    // Platform
    if (bio.platform === 'web') {
      addEntry('Biometrics', 'Web Platform', hourSettings.biometrics.webPlatform)
    }
    if (bio.platform === 'in_app') {
      addEntry('Biometrics', 'In-App Platform', hourSettings.biometrics.inAppPlatform)
    }
    if (bio.platform === 'both') {
      addEntry('Biometrics', 'Web + In-App Platform', hourSettings.biometrics.bothPlatforms)
    }

    // Branding
    if (bio.rebrandingHostedOneSDK) {
      addEntry('Biometrics', 'Rebranding Hosted OneSDK', hourSettings.biometrics.rebrandingHostedOneSDK)
    }
    if (bio.rebrandingDaon) {
      addEntry('Biometrics', 'Rebranding Daon', hourSettings.biometrics.rebrandingDaon)
    }
    if (bio.extendedBranding) {
      addEntry('Biometrics', 'Extended Branding', hourSettings.biometrics.extendedBranding)
    }
    if (bio.customRedirectPage) {
      addEntry('Biometrics', 'Custom Redirect Page', hourSettings.biometrics.customRedirectPage)
    }
    if (bio.customConsents) {
      addEntry('Biometrics', 'Custom Consents', hourSettings.biometrics.customConsents)
    }

    if (bio.vendor && bio.vendor !== 'frankieone') {
      addEntry('Biometrics', `Vendor Integration (${bio.vendor})`, hourSettings.biometrics.vendorIntegration)
    }
  }

  // === KYB Configuration ===
  const kyb = scope.kybConfig
  if (kyb.enabled === 'yes') {
    addEntry('KYB', 'Base Setup', hourSettings.kyb.baseSetup)

    // AU Checks
    if (kyb.auChecks.amlStandard) {
      addEntry('KYB', 'AML Standard', hourSettings.kyb.amlStandard)
    }
    if (kyb.auChecks.amlEnhanced) {
      addEntry('KYB', 'AML Enhanced', hourSettings.kyb.amlEnhanced)
    }
    if (kyb.auChecks.asicInfo) {
      addEntry('KYB', 'ASIC Information', hourSettings.kyb.asicInfo)
    }
    if (kyb.auChecks.ubo) {
      addEntry('KYB', 'UBO Discovery', hourSettings.kyb.ubo)
    }
    if (kyb.auChecks.creditReport) {
      addEntry('KYB', 'Business Credit Report', hourSettings.kyb.creditScore)
    }

    // KYC for Individuals
    if (kyb.kycForIndividualsRequired === 'yes') {
      addEntry('KYB', 'KYC for Individuals', hourSettings.kyb.roleDirectors + hourSettings.kyb.roleShareholders + hourSettings.kyb.roleBeneficialOwners)
    }

    // Entity Types
    const entityTypeCount = Object.values(kyb.entityTypeSplit).filter((v) => v > 0).length
    if (entityTypeCount > 0) {
      addEntry('KYB', `Entity Types (${entityTypeCount})`,
        hourSettings.kyb.perEntityType * entityTypeCount)
    }

    // International
    if (kyb.internationalCountryProducts.length > 0) {
      addEntry('KYB', `International Countries (${kyb.internationalCountryProducts.length})`,
        hourSettings.kyb.internationalPerCountry * kyb.internationalCountryProducts.length)
    }

    // KYB — Trust Analyser
    if (isYes(kyb.trustAnalyserEnabled)) {
      addEntry('KYB', 'Trust Analyser', hourSettings.kyb.trustAnalyserSetup)
    }

    // KYB — Onboarding Form
    if (isYes(kyb.onboardingFormEnabled)) {
      addEntry('KYB', 'Onboarding Form', hourSettings.kyb.onboardingFormSetup)
      if (isYes(kyb.onboardingFormBranded)) {
        addEntry('KYB', 'Onboarding Form Branding', hourSettings.kyb.onboardingFormBranding)
      }
    }

    // KYB — AML Entity Screening
    if (isYes(kyb.amlEntityScreening)) {
      addEntry('KYB', 'AML Entity Screening', hourSettings.kyb.amlEntityScreening)
    }

    // KYB — UBO Workflow
    if (isYes(kyb.uboConsentAware) || isYes(kyb.uboSmsPhoneAware)) {
      addEntry('KYB', 'UBO Workflow Configuration', hourSettings.kyb.uboWorkflowSetup)
    }

    // KYB — Aging Configuration
    if (kyb.agingEnabled) {
      addEntry('KYB', 'Aging Configuration', hourSettings.kyb.agingSetup)
    }
  }

  // === Workflows Configuration ===
  const workflows = scope.workflowsConfig
  if (workflows && workflows.length > 0) {
    workflows.forEach((wf) => {
      if (wf.kind === 'complex') {
        addEntry('Workflows', `Complex Workflow: ${wf.name || 'Unnamed'}`, hourSettings.workflows.perComplexWorkflow)
      } else {
        // Builder workflow
        addEntry('Workflows', `Workflow: ${wf.name || 'Unnamed'}`, hourSettings.workflows.perBuilderWorkflow)

        // Count steps in this workflow
        const builderWf = wf as { steps: Array<{ type: string; cascadeSources?: string[]; branches?: Array<unknown> }> }
        if (builderWf.steps && builderWf.steps.length > 0) {
          addEntry('Workflows', `  Steps (${builderWf.steps.length})`,
            hourSettings.workflows.perWorkflowStep * builderWf.steps.length)

          // Count cascade sources across KYC steps
          const totalCascadeSources = builderWf.steps.reduce((sum, step) => {
            if (step.type === 'kyc' && Array.isArray(step.cascadeSources)) {
              return sum + step.cascadeSources.length
            }
            return sum
          }, 0)
          if (totalCascadeSources > 0) {
            addEntry('Workflows', `  Cascade Sources (${totalCascadeSources})`,
              hourSettings.workflows.perCascadeSource * totalCascadeSources)
          }

          // Count risk branches
          const totalRiskBranches = builderWf.steps.reduce((sum, step) => {
            if (step.type === 'risk' && Array.isArray(step.branches)) {
              return sum + step.branches.length
            }
            return sum
          }, 0)
          if (totalRiskBranches > 0) {
            addEntry('Workflows', `  Risk Branches (${totalRiskBranches})`,
              hourSettings.workflows.perRiskBranch * totalRiskBranches)
          }
        }
      }
    })
  }

  // === Recipes & Rules ===
  const recipes = scope.recipesConfig
  if (recipes.length > 1) {
    addEntry('Recipes', `Additional Recipes (${recipes.length - 1})`,
      hourSettings.recipesRules.perRecipe * (recipes.length - 1))
  }

  const rules = scope.rulesConfig
  if (rules.duplicateRules === 'custom') {
    addEntry('Rules', 'Custom Duplicate Rules', hourSettings.recipesRules.customDuplicateRules)
  }
  if (rules.blocklistRules === 'custom') {
    addEntry('Rules', 'Custom Blocklist Rules', hourSettings.recipesRules.customBlocklistRules)
  }

  // === Risk Configuration ===
  const risk = scope.riskConfig
  if (rules.riskRatingSystem && rules.riskRatingSystem !== 'unknown') {
    addEntry('Risk', 'Risk Rating Setup', hourSettings.risk.baseSetup)
  }
  if (risk.customFactors && risk.customFactors.length > 0) {
    addEntry('Risk', `Custom Risk Factors (${risk.customFactors.length})`,
      hourSettings.risk.perCustomRiskFactor * risk.customFactors.length)
  }
  if (risk.scoringMatrix && risk.scoringMatrix.length > 0) {
    addEntry('Risk', 'Scoring Matrix Configuration', hourSettings.risk.scoringMatrixSetup)
  }
  if (risk.individualModel && risk.individualModel.length > 0) {
    addEntry('Risk', 'Individual Risk Model', hourSettings.risk.individualModelSetup)
  }

  // === Fraud & Transaction Monitoring ===
  if (isYes(rules.fraudEnabled)) {
    addEntry('Fraud & TXM', 'Fraud Configuration', hourSettings.fraudTxm.fraudSetup)
  }
  if (isYes(rules.transactionMonitoringEnabled)) {
    addEntry('Fraud & TXM', 'Transaction Monitoring', hourSettings.fraudTxm.transactionMonitoringSetup)
  }

  // === General — Additional Configuration ===
  const gi = scope.generalInfo

  // Integration Type
  if (gi.integrationType === 'api' || gi.integrationType === 'both') {
    addEntry('General', 'API Integration Setup', hourSettings.general.integrationApiSetup)
  }
  if (gi.integrationType === 'portal' || gi.integrationType === 'both') {
    addEntry('General', 'Portal Integration Setup', hourSettings.general.integrationPortalSetup)
  }

  // SSO Access
  if (gi.ssoAccess === 'yes') {
    addEntry('General', 'SSO Configuration', hourSettings.general.ssoSetup)
  }

  // AI Features
  if (gi.aiFeatures === 'yes') {
    addEntry('General', 'AI Features Setup', hourSettings.general.aiFeatures)
  }

  // Custom Work Items
  const customWorks = gi.customWorks?.filter((cw) => cw.name) || []
  if (customWorks.length > 0) {
    addEntry('General', `Custom Work Items (${customWorks.length})`,
      hourSettings.general.perCustomWorkItem * customWorks.length)
  }

  // === Testing & Training ===
  addEntry('General', 'Testing', hourSettings.general.testing)
  addEntry('General', 'Training', hourSettings.general.training)
  addEntry('General', 'Documentation', hourSettings.general.documentation)

  return entries
}

export function calculateTotalHours(entries: GeneratedHourEntry[]): number {
  return entries.reduce((sum, entry) => sum + entry.hours, 0)
}

export function groupEntriesBySection(entries: GeneratedHourEntry[]): Record<string, GeneratedHourEntry[]> {
  return entries.reduce((groups, entry) => {
    if (!groups[entry.section]) {
      groups[entry.section] = []
    }
    groups[entry.section].push(entry)
    return groups
  }, {} as Record<string, GeneratedHourEntry[]>)
}
